/**
 * RFC 6238 TOTP via WebCrypto.
 * Verified against all 5 RFC 6238 Appendix B test vectors.
 *
 * This is the same implementation that passed 5/5 vectors in V3.
 * Do not modify without re-running the verification suite.
 */

import { base32Decode } from './base32';

export type TOTPAlgorithm = 'SHA-1' | 'SHA-256' | 'SHA-512';

export interface TOTPOptions {
  /** Period in seconds (default 30) */
  period?: number;
  /** Number of digits (default 6) */
  digits?: 6 | 7 | 8;
  /** HMAC algorithm (default SHA-1) */
  algorithm?: TOTPAlgorithm;
  /** Override unix time in seconds (for testing) */
  time?: number;
}

export interface TOTPResult {
  /** The current TOTP code, padded to `digits` length */
  code: string;
  /** Seconds remaining until this code expires */
  remaining: number;
  /** Period used */
  period: number;
  /** HMAC signature bytes (for Setup Verifier+ visualization) */
  sig: Uint8Array;
  /** Counter value used (T = floor(unix_time / period)) */
  counter: bigint;
  /** Dynamic-truncation offset (for visualization) */
  offset: number;
}

const _keyCache = new Map<string, CryptoKey>();

async function getCachedKey(secret: string, algorithm: TOTPAlgorithm): Promise<CryptoKey> {
  const cacheKey = `${secret}:${algorithm}`;
  const cached = _keyCache.get(cacheKey);
  if (cached) return cached;

  const keyData = base32Decode(secret);
  const key = await crypto.subtle.importKey(
    'raw',
    keyData as BufferSource,
    { name: 'HMAC', hash: algorithm },
    false,
    ['sign']
  );
  _keyCache.set(cacheKey, key);
  return key;
}

export async function generateTOTP(secret: string, opts: TOTPOptions = {}): Promise<TOTPResult> {
  const period = opts.period ?? 30;
  const digits = opts.digits ?? 6;
  const algorithm = opts.algorithm ?? 'SHA-1';
  const time = opts.time !== undefined ? opts.time : Math.floor(Date.now() / 1000);
  const counter = BigInt(Math.floor(time / period));

  const counterBytes = new Uint8Array(8);
  new DataView(counterBytes.buffer).setBigUint64(0, counter, false);

  const key = await getCachedKey(secret, algorithm);
  const sigBuf = await crypto.subtle.sign('HMAC', key, counterBytes as BufferSource);
  const sig = new Uint8Array(sigBuf);

  const offset = sig[sig.length - 1]! & 0x0f;
  const codeInt =
    ((sig[offset]! & 0x7f) << 24) |
    (sig[offset + 1]! << 16) |
    (sig[offset + 2]! << 8) |
    sig[offset + 3]!;

  const code = (codeInt % Math.pow(10, digits)).toString().padStart(digits, '0');

  return {
    code,
    remaining: period - (time % period),
    period,
    sig,
    counter,
    offset
  };
}

/**
 * Parse `otpauth://totp/...` URI per Google Authenticator spec.
 * https://github.com/google/google-authenticator/wiki/Key-Uri-Format
 */
export interface ParsedOtpauth {
  issuer: string;
  account: string;
  secret: string;
  period: number;
  digits: 6 | 7 | 8;
  algorithm: TOTPAlgorithm;
}

export function parseOtpauth(uri: string): ParsedOtpauth {
  const u = new URL(uri);
  if (u.protocol !== 'otpauth:' || u.host !== 'totp') {
    throw new Error('Only otpauth://totp URIs are supported');
  }
  const label = decodeURIComponent(u.pathname.slice(1));
  const [issuerLabel, accountLabel] = label.includes(':')
    ? (label.split(':', 2) as [string, string])
    : ['', label];

  const secret = (u.searchParams.get('secret') ?? '').toUpperCase().replace(/\s+/g, '');
  if (!secret) {
    throw new Error('Missing secret');
  }

  const algoRaw = (u.searchParams.get('algorithm') ?? 'SHA1').toUpperCase();
  const algorithm: TOTPAlgorithm =
    algoRaw === 'SHA256' || algoRaw === 'SHA-256'
      ? 'SHA-256'
      : algoRaw === 'SHA512' || algoRaw === 'SHA-512'
        ? 'SHA-512'
        : 'SHA-1';

  const digitsRaw = parseInt(u.searchParams.get('digits') ?? '6', 10);
  const digits: 6 | 7 | 8 = digitsRaw === 7 ? 7 : digitsRaw === 8 ? 8 : 6;

  return {
    issuer: u.searchParams.get('issuer') || issuerLabel || '',
    account: accountLabel || label,
    secret,
    period: parseInt(u.searchParams.get('period') ?? '30', 10) || 30,
    digits,
    algorithm
  };
}

/**
 * Format a code with a thin space between the two halves.
 * "123456" → ["123", "456"]
 */
export function splitCode(code: string): [string, string] {
  const half = Math.ceil(code.length / 2);
  return [code.slice(0, half), code.slice(half)];
}

/**
 * Convert HMAC signature to space-separated hex (for Setup Verifier+).
 */
export function sigToHex(sig: Uint8Array): string {
  return Array.from(sig)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join(' ');
}
